import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { SUBJECTAREA_TYPE } from '../../constants/api';
import {
  createSubjectAreaInitialize,
  deleteSubjectAreaInitialize,
  updateSubjectAreaInitialize,
} from '../actions/api';
import { subjectAreaByIdSelector } from '../apiFeatures/subjectAreaSlice';
import { parseApiError } from '../globalMessageSlice';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const updateSubjectArea = createAction(
  'page/administrationSubjectArea/changeSubjectAreaIsActive',
);
const setSelectedSubjectAreaId = createAction(
  'page/administrationSubjectArea/setSelectedSubjectAreaId',
);
const deleteSubjectArea = createAction(
  'page/administrationSubjectArea/deleteSubjectArea',
);
const setSubjectAreaBeingUpdated = createAction(
  'page/administrationSubjectArea/subjectAreaBeingUpdated',
);
const setIsPanelOpen = createAction(
  'page/administrationSubjectArea/setIsPanelOpen',
);
const createSubjectArea = createAction(
  'page/administrationSubjectArea/createSubjectArea',
);
const setSubjectAreaBeingCreated = createAction(
  'page/administrationSubjectArea/subjectAreaBeingCreated',
);

// ---------- REDUCERS -----------
const subjectAreaAdministrationPageReducer = createReducer(
  {
    selectedSubjectAreaId: null,
    isPanelOpen: false,
    isUpdatingSubjectArea: false,
    isCreatingSubjectArea: false,
  },
  (builder) => {
    builder
      .addCase(setSelectedSubjectAreaId, (state, action) => {
        state.selectedSubjectAreaId = action.payload;
      })
      .addCase(setSubjectAreaBeingUpdated, (state, action) => {
        state.isUpdatingSubjectArea = action.payload;
      })
      .addCase(setIsPanelOpen, (state, action) => {
        state.isPanelOpen = action.payload;
      })
      .addCase(setSubjectAreaBeingCreated, (state, action) => {
        state.isCreatingSubjectArea = action.payload;
      });
  },
);
// ----------- Sagas -------------
export function* subjectAreaAdministrationPageSaga() {
  yield all([
    fork(changeSubjectAreaIsActiveSaga),
    fork(deleteSubjectAreaSaga),
    fork(createSubjectAreaSaga),
  ]);
}

function* changeSubjectAreaIsActiveSaga() {
  yield takeLatest(updateSubjectArea.type, function* (action) {
    yield put(setSubjectAreaBeingUpdated(true));
    const { subjectAreaId, isActive } = action.payload;

    const subjectArea = yield select(subjectAreaByIdSelector(subjectAreaId));

    const newSubjectArea = Object.assign({}, subjectArea, {
      attributes: { ...subjectArea.attributes, isActive: isActive },
      relationships: { ...subjectArea.relationships },
    });

    const updateSubjectAreaInitAction = yield put(
      updateSubjectAreaInitialize({
        subjectAreaId,
        changes: newSubjectArea,
      }),
    );

    const updateSubjectAreaResult = yield awaitApiCall(
      updateSubjectAreaInitAction,
    );

    if (updateSubjectAreaResult.error) {
      yield put(parseApiError(updateSubjectAreaResult.error.payload));
    }
    yield put(setSubjectAreaBeingUpdated(false));
  });
}

function* deleteSubjectAreaSaga() {
  yield takeLatest(deleteSubjectArea.type, function* (action) {
    yield put(setSubjectAreaBeingUpdated(true));

    const subjectAreaId = action.payload;

    const deleteSubjectAreaInitAction = yield put(
      deleteSubjectAreaInitialize(subjectAreaId),
    );

    const deleteSubjectAreaResult = yield awaitApiCall(
      deleteSubjectAreaInitAction,
    );

    if (deleteSubjectAreaResult.success) {
      yield put(setSelectedSubjectAreaId(null));
    } else if (deleteSubjectAreaResult.error) {
      yield put(parseApiError(deleteSubjectAreaResult.error.payload));
    }

    yield put(setSubjectAreaBeingUpdated(false));
  });
}

function* createSubjectAreaSaga() {
  yield takeLatest(createSubjectArea.type, function* (action) {
    yield put(setSubjectAreaBeingCreated(true));
    const title = action.payload;

    const newSubjectAreaModel = {
      type: SUBJECTAREA_TYPE,
      attributes: {
        name: title,
        isActive: true,
      },
      relationships: {},
    };

    const createSubjectAreaInitAction = yield put(
      createSubjectAreaInitialize({ data: newSubjectAreaModel }),
    );

    const createSubjectAreaResult = yield awaitApiCall(
      createSubjectAreaInitAction,
    );

    if (createSubjectAreaResult.error) {
      yield put(parseApiError(createSubjectAreaResult.error.payload));
    }
    yield put(setIsPanelOpen(false));
    yield put(setSubjectAreaBeingCreated(false));
  });
}
// ----------- Selectors -----------

const subjectAreaAdministrationState = (state) =>
  state.page.subjectAreaAdministration;

const isUpdatingSubjectAreaSelector = createSelector(
  subjectAreaAdministrationState,
  (state) => state.isUpdatingSubjectArea,
);

const isPanelOpenSelector = createSelector(
  subjectAreaAdministrationState,
  (state) => state.isPanelOpen,
);

const selectedSubjectAreaIdSelector = createSelector(
  subjectAreaAdministrationState,
  (state) => state.selectedSubjectAreaId,
);

const isCreatingSubjectAreaSelector = createSelector(
  subjectAreaAdministrationState,
  (state) => state.isCreatingSubjectArea,
);

export {
  updateSubjectArea,
  setSelectedSubjectAreaId,
  selectedSubjectAreaIdSelector,
  deleteSubjectArea,
  isUpdatingSubjectAreaSelector,
  isPanelOpenSelector,
  setIsPanelOpen,
  createSubjectArea,
  isCreatingSubjectAreaSelector,
};

export default subjectAreaAdministrationPageReducer;
